import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Offcanvas, Spinner, Table } from 'react-bootstrap';
import produce from 'immer';

import SingleDimensionFilter from '@pages/Analysis/SingleDimensionFilter.component';
import { Option } from '@components/form/Dropdown.component';
import { AnalysisDimensionFilter } from '@models/analysis';
import { useDimensions } from '@models/reportBuilder';
import { ContextItem } from '@models/context';
import { deleteContextItem, saveContextItem, useContextItems } from '@stores/data.store';
import { ApiError } from '@services/api/api.service';
import { PageContent, PageContentHeader, PageContentInner, Pane, PaneContent } from '@pages/PageStructure.component';
import PageStructure from '@pages/PageStructure.component';
import SaveButton from '@components/button/SaveButton.component';

const comparatorOptions: Option[] = [
    { value: 'IN', label: 'is one of' },
    { value: 'EQUALS', label: 'equals' },
    { value: 'NOT_EQUALS', label: 'is not equal to' },
    { value: 'CONTAINS', label: 'contains' },
    { value: 'NOT_CONTAINS', label: 'does not contain' },
    { value: 'GTE', label: 'on or after' },
    { value: 'LTE', label: 'on or before' },
    { value: 'GT', label: 'after' },
    { value: 'LT', label: 'before' },
    { value: 'EMPTY', label: 'is empty' },
    { value: 'NOT_EMPTY', label: 'is not empty' },
];

const comparatorsWithoutValues = new Set([
    'EMPTY',
    'NOT_EMPTY',
    'VALID_DATE',
    'INVALID_DATE',
    'VALID_NUMBER',
    'INVALID_NUMBER',
]);

const defaultFilter = (): AnalysisDimensionFilter => ({
    dimension_id: '',
    comparator: 'IN',
    value: [],
});

const blankContext = (): ContextItem => ({
    id: null,
    name: '',
    description: '',
    dimension_filters: [],
});

const normalizeFiltersForEdit = (filters: AnalysisDimensionFilter[]): AnalysisDimensionFilter[] => {
    return (filters || []).map((filter) => {
        if (filter.comparator === 'IN') {
            if (Array.isArray(filter.value)) {
                return filter;
            }
            if (filter.value == null || filter.value === '') {
                return { ...filter, value: [] };
            }
            return { ...filter, value: [String(filter.value)] };
        }

        if (comparatorsWithoutValues.has(filter.comparator)) {
            return { ...filter, value: undefined };
        }

        if (Array.isArray(filter.value)) {
            return { ...filter, value: filter.value.length ? String(filter.value[0]) : '' };
        }

        return { ...filter, value: filter.value ?? '' };
    });
};

const prepareFiltersForSave = (filters: AnalysisDimensionFilter[]) => {
    return filters
        .filter((filter) => filter.dimension_id)
        .map((filter) => {
            if (filter.comparator === 'IN') {
                if (Array.isArray(filter.value)) {
                    return filter;
                }
                if (filter.value == null || filter.value === '') {
                    return { ...filter, value: [] };
                }
                return { ...filter, value: [String(filter.value)] };
            }

            if (comparatorsWithoutValues.has(filter.comparator)) {
                return { ...filter, value: undefined };
            }

            if (Array.isArray(filter.value)) {
                return { ...filter, value: filter.value.length ? String(filter.value[0]) : '' };
            }

            return filter;
        });
};

const valueToDisplay = (filter: AnalysisDimensionFilter): string => {
    if (comparatorsWithoutValues.has(filter.comparator)) {
        return '';
    }
    if (Array.isArray(filter.value)) {
        return filter.value.join(', ');
    }
    return filter.value ? String(filter.value) : '';
};

const ContextListPage = () => {
    const { data: contextItems, isLoading, isFetching } = useContextItems();
    const { data: dimensions } = useDimensions();

    const [showDrawer, setShowDrawer] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formState, setFormState] = useState<ContextItem>(blankContext());

    const dimensionOptions = useMemo<Option[]>(() => {
        if (!dimensions) {
            return [];
        }
        return dimensions.map((dimension) => ({
            value: String(dimension.id),
            label: dimension.name,
        }));
    }, [dimensions]);

    const dimensionLabelById = useMemo<Record<string, string>>(() => {
        if (!dimensions) {
            return {};
        }
        return dimensions.reduce((acc, dimension) => {
            acc[String(dimension.id)] = dimension.name;
            return acc;
        }, {} as Record<string, string>);
    }, [dimensions]);

    const handleOpenNew = useCallback(() => {
        setFormState(blankContext());
        setError(null);
        setShowDrawer(true);
    }, []);

    const handleEdit = useCallback((contextItem: ContextItem) => {
        setFormState({
            ...contextItem,
            dimension_filters: normalizeFiltersForEdit(contextItem.dimension_filters || []),
        });
        setError(null);
        setShowDrawer(true);
    }, []);

    const handleCloseDrawer = useCallback(() => {
        setShowDrawer(false);
        setError(null);
        setSaving(false);
    }, []);

    const handleFilterChange = useCallback((idx: number, updated: AnalysisDimensionFilter) => {
        setFormState((current) =>
            produce(current, (draft) => {
                draft.dimension_filters[idx] = updated;
            })
        );
    }, []);

    const handleAddFilter = useCallback(() => {
        setFormState((current) =>
            produce(current, (draft) => {
                draft.dimension_filters.push(defaultFilter());
            })
        );
    }, []);

    const handleRemoveFilter = useCallback((idx: number) => {
        setFormState((current) =>
            produce(current, (draft) => {
                draft.dimension_filters.splice(idx, 1);
            })
        );
    }, []);

    const handleFieldChange = useCallback(<K extends keyof ContextItem>(field: K, value: ContextItem[K]) => {
        setFormState((current) =>
            produce(current, (draft) => {
                draft[field] = value;
            })
        );
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            const preparedFilters = prepareFiltersForSave(formState.dimension_filters || []);
            await saveContextItem({
                ...formState,
                dimension_filters: preparedFilters,
            });
            await new Promise((resolve) => setTimeout(resolve, 1000));
            setShowDrawer(false);
        } catch (err) {
            if (err instanceof ApiError && err.payload?.msg) {
                setError(err.payload.msg);
            } else if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Unable to save context item.');
            }
        } finally {
            setSaving(false);
        }
    }, [formState]);

    const handleDelete = useCallback(async (contextItem: ContextItem) => {
        if (!contextItem.id) {
            return;
        }
        if (!window.confirm(`Delete "${contextItem.name}"?`)) {
            return;
        }
        try {
            await deleteContextItem(contextItem);
        } catch (err) {
            if (err instanceof ApiError && err.payload?.msg) {
                setError(err.payload.msg);
            } else if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Unable to delete context item.');
            }
        }
    }, []);

    useEffect(() => {
        if (!showDrawer) {
            setFormState(blankContext());
        }
    }, [showDrawer]);

    return (
        <PageStructure>
            <PageContent>
                <PageContentHeader>
                    <div className="d-flex center-vertically">
                        <div style={{width:'40%'}}>
                            <h1 className="mb-0">Context</h1>
                            <div className="text-muted font-13">Create contextual events or notes that can be surfaced automatically in analyses and AI responses.</div>
                        </div>
                        <div className="flex-1"></div>
                        <div className="">
                            <Button onClick={handleOpenNew}>
                                <i className="mdi mdi-plus-thick me-1" />
                                New Context
                            </Button>
                        </div>
                    </div>
                </PageContentHeader>
                <PageContentInner hasHeader>
                <div className="p-3">
                
                {(isLoading) && (
                    <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Loading context...</span>
                    </div>
                )}

                {!isLoading && contextItems && contextItems.length === 0 && (
                    <div className="text-muted text-center py-5">
                        <p className="mb-2">No context items yet.</p>
                        <Button variant="link" onClick={handleOpenNew}>
                            Add your first context item
                        </Button>
                    </div>
                )}

                {!isLoading && contextItems && contextItems.length > 0 && (
                    <Table bordered responsive>
                        <thead className="bg-light">
                            <tr>
                                <th style={{ width: '20%' }}>Name</th>
                                <th style={{ width: '40%' }}>Description</th>
                                <th>Filters</th>
                                <th style={{ width: '20%' }} className="text-end">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {contextItems.map((item) => (
                                <tr key={item.id}>
                                    <td className="align-middle">{item.name}</td>
                                    <td className="align-middle text-break">{item.description}</td>
                                    <td className="align-middle">
                                        {(item.dimension_filters || []).length === 0 && (
                                            <span className="text-muted">No filters</span>
                                        )}
                                        {(item.dimension_filters || []).length > 0 && (
                                            <ul className="mb-0 ps-3">
                                                {item.dimension_filters.map((filter, idx) => (
                                                    <li key={idx}>
                                                        <strong>{dimensionLabelById[filter.dimension_id] || filter.dimension_id}</strong>{' '}
                                                        <span className="text-muted">{filter.comparator}</span>{' '}
                                                        <span>{valueToDisplay(filter)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </td>
                                    <td className="align-middle text-end">
                                        <Button size="sm" variant="light" className="me-1" onClick={() => handleEdit(item)}>
                                            <i className="mdi mdi-pencil" /> Edit
                                        </Button>
                                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(item)}>
                                            <i className="mdi mdi-delete" /> Delete
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
                </div>
                </PageContentInner>

                <Offcanvas placement="end" show={showDrawer} onHide={handleCloseDrawer} backdrop="static">
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title>{formState.id ? 'Edit Context' : 'New Context'}</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <Pane>
                            <PaneContent>
                                <div className="p-2">
                                {error && <Alert variant="danger">{error}</Alert>}
                                    <Form>
                                        <Form.Group className="mb-3" controlId="context-name">
                                            <Form.Label>Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formState.name}
                                                onChange={(event) => handleFieldChange('name', event.target.value)}
                                                placeholder="Prime Day 2025"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3" controlId="context-description">
                                            <Form.Label>Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={formState.description || ''}
                                                onChange={(event) => handleFieldChange('description', event.target.value)}
                                                placeholder="Additional context to share with analysts or AI assistants."
                                            />
                                        </Form.Group>
                                    </Form>

                                    <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                                    <Form.Label className="mb-0">Dimension Filters</Form.Label>
                                    
                                        <Button variant="light" size="sm" onClick={handleAddFilter}>
                                            <i className="mdi mdi-plus-thick me-1" />
                                            Add Filter
                                        </Button>
                                    </div>

                                    {formState.dimension_filters.length === 0 && (
                                        <p className="text-muted">Add one or more filters to describe when this context applies.</p>
                                    )}

                                    {formState.dimension_filters.length > 0 && (
                                        <div className="list-group mb-2">
                                            {formState.dimension_filters.map((filter, idx) => (
                                                <div key={idx} className="list-group-item py-3">
                                                    <SingleDimensionFilter
                                                        displayType="form"
                                                        dimensionFilter={filter}
                                                        dimensionOptions={dimensionOptions}
                                                        allowComparatorSelection
                                                        comparatorOptions={comparatorOptions}
                                                        onChange={(updated) => handleFilterChange(idx, updated)}
                                                        onDelete={() => handleRemoveFilter(idx)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-end mt-4">
                                        <Button variant="light" className="me-1" onClick={handleCloseDrawer} disabled={saving}>
                                            Cancel
                                        </Button>
                                        <SaveButton
                                            onClick={handleSave}
                                            disabled={saving || !formState.name}
                                            text="Save Context"
                                        />
                                    </div>
                                </div>
                            </PaneContent>
                        </Pane>
                        
                    </Offcanvas.Body>
                </Offcanvas>
            </PageContent>
        </PageStructure>
    );
};

export default ContextListPage;

